import { App } from 'app';
import { BusinessLogicProviders } from 'app/components/ReactContexts';
import { GlobalErrorContext } from 'app/components/ReactContexts/globalErrorContext';
import { FullscreenWordmarkLoader } from 'app/components/loaders';
import { msalInstance } from 'msalConfig/msalConfig';
import React, { useContext, useEffect, useState } from 'react';
import { AuthService } from 'services/AuthService';
import { api, setupAxiosAuthInterceptors } from 'services/api';
import store from 'services/store';
import { FeatureName } from 'types/entities/MobileSettings';

interface InitializationResult {
  authService: AuthService;
}

export default function AppInitializer() {
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>(
    'loading',
  );

  const { showAppInitializationErrorModal } = useContext(GlobalErrorContext);

  const [initialization, setInitialization] =
    useState<InitializationResult | null>(null);

  const [getMobileSettings] = api.useLazyGetMobileSettingsQuery();
  const [getUserInfoV1] = api.useLazyGetUserInfoV1Query();
  const [getUserInfoV2] = api.useLazyGetUserInfoV2Query();
  const [getUserSettings] = api.useLazyGetUserSettingsQuery();

  useEffect(() => {
    async function initializeApp() {
      try {
        const mobileSettings = await getMobileSettings().unwrap();
        const isSingleSignOnFeatureEnabled =
          !mobileSettings.disabledFeatureFlags?.includes(
            FeatureName.SINGLE_SIGN_ON,
          );

        const authService = new AuthService(
          msalInstance,
          isSingleSignOnFeatureEnabled,
          store.dispatch,
        );

        setupAxiosAuthInterceptors(authService);

        await authService.handleLoginRedirect();

        if (authService.isAuthenticated()) {
          // To avoid a race condition when retrieving the token, we request it in advance. This can be optimized in the future.
          // Until we figure out why MSAL doesn’t cache the token, we’ll handle it manually.
          await authService.getAccessToken();

          const getUserInfo = isSingleSignOnFeatureEnabled
            ? getUserInfoV2
            : getUserInfoV1;

          const promises = [getUserInfo(), getUserSettings()];
          await Promise.all(promises);
        }
        setStatus('success');
        setInitialization({ authService });
      } catch (e) {
        console.error('Failed to initialize the application:', e);
        setStatus('error');
        showAppInitializationErrorModal();
      }
    }

    initializeApp();
  }, [getMobileSettings, getUserInfoV1, getUserInfoV2, getUserSettings]);

  if (status === 'loading') {
    return <FullscreenWordmarkLoader />;
  }

  return (
    <BusinessLogicProviders authService={initialization!.authService}>
      <App />
    </BusinessLogicProviders>
  );
}
